import { useState, type ElementType } from 'react';
import { clsx } from 'clsx';
import {
  AlertTriangle,
  Anchor,
  ChevronRight,
  LineChart,
  MessageSquare,
  ShieldCheck,
  Ship,
} from 'lucide-react';
import { Button } from '@/components/ui';
import { VesselBadge } from '@/components/fixtures/VesselBadge';
import type { Position } from '@/types/positionList.types';
import {
  formatPositionDate,
  formatPoolPoints,
  getVesselSizeInfo,
  truncateText,
} from '@/utils/positionListUtils';

interface PositionRowProps {
  position: Position;
  onViewDetails: (position: Position) => void;
}

function formatDateCompact(dateString: string): string {
  if (!dateString || dateString.trim() === '' || dateString.trim() === ' ') {
    return '';
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return dateString;
  }
}

function BoolPill({ active }: { active: boolean }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums',
        active
          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/45 dark:text-emerald-300'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
      )}
    >
      {active ? 'Yes' : 'No'}
    </span>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-gray-200/90 bg-white/95 px-3 py-2.5 text-center shadow-sm dark:border-dark-600 dark:bg-dark-800/90">
      <div className="type-caption mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="truncate text-base font-semibold tabular-nums text-gray-900 dark:text-white sm:text-lg">
        {value}
      </div>
    </div>
  );
}

function ExpandedSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: ElementType;
  children: React.ReactNode;
}) {
  return (
    <section className="min-w-0">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
          <Icon className="h-3.5 w-3.5" aria-hidden />
        </span>
        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-700 dark:text-gray-200">
          {title}
        </h4>
      </div>
      <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200/90 bg-white/90 shadow-sm dark:divide-dark-700/90 dark:border-dark-600 dark:bg-dark-800/80">
        {children}
      </div>
    </section>
  );
}

function SpecRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex flex-col gap-1 px-3 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:py-2">
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <span
        className={clsx(
          'min-w-0 text-sm font-medium leading-snug text-gray-900 dark:text-white sm:max-w-[65%] sm:text-right',
          valueClassName
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function PositionRow({ position, onViewDetails }: PositionRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => setIsExpanded(!isExpanded);
  const handleRowKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpand();
    }
  };
  const openDate = position.positionListDate || position.date;

  const statusBadgeProps = {
    onSubs: position.bitOnSubs,
    nextDryDockWarning: position.nextDryDockWarning,
    onHoldWithCargo: position.bitOnHoldWithCargo,
    onHoldWithoutCargo: position.bitOnHoldNoCargo,
    sireWarning: position.sireWarning,
    scrubberInstalled: position.hasScrubber === 'Yes',
    age15Plus: position.ageWarning === 1,
  } as const;

  const rowShell = clsx(
    'cursor-pointer bg-white px-2 py-1.5 transition-colors dark:bg-dark-800',
    'hover:bg-gray-50 dark:hover:bg-dark-700',
    isExpanded && 'bg-gray-50 dark:bg-dark-700/50'
  );

  return (
    <div
      className="overflow-hidden rounded-lg border border-gray-200 dark:border-dark-700"
      style={{ containerType: 'inline-size' }}
    >
      {/* Mobile — compact: badges inline with name, single-line meta */}
      <div
        className={clsx(rowShell, '!px-2 !py-2 md:hidden')}
        onClick={toggleExpand}
        onKeyDown={handleRowKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="flex gap-1.5">
          <div className="flex w-5 shrink-0 items-center justify-center self-start pt-1">
            <ChevronRight
              className={clsx(
                'h-4 w-4 flex-shrink-0 text-gray-400 transition-transform duration-200',
                isExpanded && 'rotate-90'
              )}
              aria-hidden
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0">
              <span className="text-base font-semibold leading-snug text-gray-900 dark:text-white">
                {position.vesselName}
              </span>
              <span className="shrink-0 text-xs leading-none text-gray-500 dark:text-gray-400">
                ({position.built})
              </span>
              <VesselBadge {...statusBadgeProps} compact flushLeft />
            </div>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0 text-xs leading-snug text-gray-600 dark:text-gray-300">
              <span className="font-medium text-gray-800 dark:text-gray-100">
                {position.lastPosition}
              </span>
              <VesselBadge onObservation={position.onObservationList} inline compact />
            </div>
            <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-0.5 border-t border-gray-100 pt-1.5 text-xs leading-snug text-gray-600 dark:border-dark-700 dark:text-gray-400">
              <span className="inline-flex items-center gap-0.5">
                <span className="shrink-0 text-gray-400">D</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  {formatDateCompact(position.date) || '—'}
                </span>
                <VesselBadge narrowLaycan={position.narrowLayCanWarning} inline compact />
              </span>
              <span className="shrink-0 text-gray-300 dark:text-gray-600" aria-hidden>
                ·
              </span>
              <span className="inline-flex items-center gap-0.5">
                <span className="shrink-0 text-gray-400">O</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">
                  {formatDateCompact(openDate) || '—'}
                </span>
                <VesselBadge market={position.shownToMarket} inline compact />
              </span>
              <span className="shrink-0 text-gray-300 dark:text-gray-600" aria-hidden>
                ·
              </span>
              <span className="inline-flex min-w-0 max-w-full flex-1 items-center gap-0.5 sm:flex-initial">
                <span className="shrink-0 text-gray-400">Act</span>
                <span className="min-w-0 truncate font-medium text-gray-800 dark:text-gray-200">
                  {position.activity ? truncateText(position.activity, 18) : '? D'}
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop */}
      <div
        className={clsx(
          rowShell,
          'hidden md:grid md:grid-cols-[24px_2fr_80px_120px_1fr_120px_120px] md:items-center md:gap-4 md:px-2 md:py-2'
        )}
        onClick={toggleExpand}
        onKeyDown={handleRowKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="flex items-center justify-center">
          <ChevronRight
            className={clsx(
              'h-5 w-5 text-gray-400 transition-transform duration-200',
              isExpanded && 'rotate-90'
            )}
            aria-hidden
          />
        </div>

        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-gray-900 dark:text-white">
            {position.vesselName}
          </span>
          <span className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
            ({position.built})
          </span>
          <VesselBadge {...statusBadgeProps} />
        </div>

        <div className="text-center text-sm text-gray-600 dark:text-gray-300">
          {position.activity ? (
            <span title={position.activity}>{truncateText(position.activity, 10)}</span>
          ) : (
            '? D'
          )}
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-300">
          {formatDateCompact(position.lastSireDate) || '—'}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
            <span className="break-words">{position.lastPosition}</span>
            <VesselBadge onObservation={position.onObservationList} inline />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
          {formatDateCompact(position.date) || '—'}
          <VesselBadge narrowLaycan={position.narrowLayCanWarning} inline />
        </div>

        <div className="flex flex-wrap items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
          <span>{formatDateCompact(openDate) || '—'}</span>
          <VesselBadge market={position.shownToMarket} inline />
        </div>
      </div>

      {isExpanded && (
        <div
          className="border-t border-gray-200 bg-gradient-to-b from-gray-50/95 to-gray-100/80 px-2 py-4 dark:border-dark-700 dark:from-dark-900/40 dark:to-dark-950/50 sm:px-4"
          onClick={(e) => e.stopPropagation()}
          role="region"
          aria-label="Position summary"
        >
          <div className="mb-4 flex items-center justify-between gap-2 border-b border-gray-200/80 pb-3 dark:border-dark-600">
            <h4 className="text-sm font-bold tracking-tight text-gray-900 dark:text-white">
              Position summary
            </h4>
            <span className="type-caption hidden text-right sm:block">Key figures & status</span>
          </div>

          <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile
              label="DWT"
              value={position.dwt ? parseInt(position.dwt, 10).toLocaleString() : '—'}
            />
            <StatTile
              label="Ballast"
              value={
                position.lastBallastSpeed != null
                  ? `${position.lastBallastSpeed.toFixed(1)} kts`
                  : '—'
              }
            />
            <StatTile
              label="Laden"
              value={
                position.lastLadenSpeed != null ? `${position.lastLadenSpeed.toFixed(1)} kts` : '—'
              }
            />
            <StatTile label="HM" value={position.hm != null ? position.hm.toFixed(2) : '—'} />
          </div>

          <div className="space-y-5">
            <ExpandedSection title="Activity & vessel" icon={Ship}>
              <SpecRow
                label="Current activity"
                value={position.activity || 'N/A'}
                valueClassName="!text-base !font-semibold !leading-snug sm:!max-w-[70%]"
              />
              <SpecRow
                label="Days since position update"
                value={
                  position.daysSinceLastPositionUpdate != null
                    ? `${position.daysSinceLastPositionUpdate} days`
                    : 'N/A'
                }
                valueClassName="tabular-nums"
              />
              <SpecRow label="Date delivered" value={formatPositionDate(position.dateDelivered)} />
              <SpecRow label="Vessel size" value={getVesselSizeInfo(position.vesselSize).name} />
              <SpecRow label="Scrubber" value={position.hasScrubber || 'N/A'} />
            </ExpandedSection>

            <ExpandedSection title="Inspections & drydock" icon={ShieldCheck}>
              <SpecRow label="Last SIRE" value={formatPositionDate(position.lastSireDate)} />
              <SpecRow label="Next drydock" value={formatPositionDate(position.nextDD)} />
              <SpecRow label="SIRE expiring" value={formatPositionDate(position.sireExpiring)} />
            </ExpandedSection>

            <ExpandedSection title="Pool & charter" icon={LineChart}>
              <SpecRow
                label="Pool points (calc.)"
                value={
                  position.poolpointCalculated != null
                    ? formatPoolPoints(position.poolpointCalculated)
                    : 'N/A'
                }
                valueClassName="tabular-nums"
              />
              <SpecRow
                label="Pool points TIFPR"
                value={
                  position.poolpointTIFPR != null
                    ? formatPoolPoints(position.poolpointTIFPR)
                    : 'N/A'
                }
                valueClassName="tabular-nums"
              />
              <SpecRow
                label="Future fixture"
                value={<BoolPill active={position.hasFutureFixture} />}
              />
              <SpecRow
                label="Fully fixed (tramos)"
                value={<BoolPill active={position.fullyFixedInTramos} />}
              />
              <SpecRow
                label="On restriction list"
                value={<BoolPill active={position.onRestrictionList === 1} />}
              />
            </ExpandedSection>

            <ExpandedSection title="Trading & routes" icon={Anchor}>
              <SpecRow
                label="Shown to market"
                value={<BoolPill active={position.shownToMarket === 1} />}
              />
              <SpecRow label="On subs" value={<BoolPill active={position.bitOnSubs === 1} />} />
            </ExpandedSection>
          </div>

          {position.comment && position.comment.trim() !== '' && (
            <div className="mt-5 overflow-hidden rounded-xl border border-blue-200/80 bg-blue-50/90 dark:border-blue-800/50 dark:bg-blue-950/30">
              <div className="flex items-center gap-2 border-b border-blue-200/60 bg-blue-100/50 px-3 py-2 dark:border-blue-800/40 dark:bg-blue-900/20">
                <MessageSquare className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                <p className="text-xs font-bold uppercase tracking-wider text-blue-900 dark:text-blue-200">
                  Comments
                </p>
              </div>
              <p className="type-body whitespace-pre-wrap break-words px-3 py-3 text-blue-900 dark:text-blue-100">
                {position.comment}
              </p>
            </div>
          )}

          {position.tradingRestrictions && position.tradingRestrictions.trim() !== '' && (
            <div className="mt-4 overflow-hidden rounded-xl border border-amber-200/90 bg-amber-50/90 dark:border-amber-800/50 dark:bg-amber-950/25">
              <div className="flex items-center gap-2 border-b border-amber-200/70 bg-amber-100/50 px-3 py-2 dark:border-amber-800/40 dark:bg-amber-900/20">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                <p className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                  Trading restrictions
                </p>
              </div>
              <p className="type-body whitespace-pre-wrap break-words px-3 py-3 text-amber-950 dark:text-amber-100">
                {position.tradingRestrictions}
              </p>
            </div>
          )}

          <div className="mt-5 flex justify-center border-t border-gray-200/80 pt-4 dark:border-dark-600 sm:justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails(position);
              }}
              rightIcon={<ChevronRight className="h-4 w-4" />}
            >
              View full details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
